import { useMemo, useState } from 'react';
import { api, type Scan } from '../api.ts';
import { message, plural } from './format.ts';

/**
 * Produce the document an MSP hands a client at the quarterly meeting.
 *
 * Its own tab rather than a control on Progress, and the reason is what a
 * period does on each screen. Progress is a live view of this client's recent
 * assessments: the window is ours, it is not named by anybody, and the table is
 * the answer. Here the period is the operator's, and the answer is a document
 * that leaves the console — so a date picker sitting above the progress table
 * would read as a filter on it, which it is not, and the two would then
 * disagree about which assessments are in view. The tab is placed straight
 * after Progress because it is the same material addressed to a different
 * reader.
 *
 * This screen decides nothing about what the review says. It reads a period,
 * says how much of it there is to describe, and opens the document; every
 * judgement in it is made by `buildServiceReview` on the server.
 */

interface ServiceReviewProps {
  tenantId: string;
  /**
   * The run log this screen already has. Used only to say how many assessments
   * fall inside the chosen period *before* the operator produces a document for
   * a client — never to decide what the document contains.
   */
  scans: Scan[];
  onError: (text: string) => void;
}

/** `YYYY-MM-DD`, from the operator's own calendar rather than from UTC. */
const asInputDate = (date: Date): string =>
  [
    String(date.getFullYear()).padStart(4, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

/**
 * The last complete calendar quarter — the period a quarterly review is
 * actually about.
 *
 * Complete, never the one running: a review of the quarter we are three weeks
 * into would be handed to a client as though it described the whole of it.
 */
const lastQuarter = (today: Date): { from: string; to: string } => {
  const quarter = Math.floor(today.getMonth() / 3);
  const year = quarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const startMonth = quarter === 0 ? 9 : (quarter - 1) * 3;
  // Day zero of the month after the quarter's last is that last day, which
  // avoids a table of month lengths and a leap-year branch.
  return {
    from: asInputDate(new Date(year, startMonth, 1)),
    to: asInputDate(new Date(year, startMonth + 3, 0)),
  };
};

/** Ninety days ending today, counted inclusively — today is one of the ninety. */
const lastNinetyDays = (today: Date): { from: string; to: string } => {
  const from = new Date(today);
  from.setDate(from.getDate() - 89);
  return { from: asInputDate(from), to: asInputDate(today) };
};

/**
 * The two instants the server will read these dates as: `from` opens its day
 * and `to` closes it, both in UTC. Duplicated here only to count assessments —
 * the period the document is bounded by is read once, on the server.
 */
const boundsOf = (from: string, to: string): { start: number; end: number } => ({
  start: Date.parse(`${from}T00:00:00.000Z`),
  end: Date.parse(`${to}T23:59:59.999Z`),
});

interface Coverage {
  /** Completed assessments this console can see inside the period. */
  readonly assessments: number;
  /**
   * Whether the run log reaches back past the start of the period.
   *
   * It holds the twenty most recent runs, so for a period older than those the
   * console genuinely cannot tell whether the client was assessed. Saying "no
   * assessments" there would talk an operator out of a document that has
   * something in it, which is the worse of the two mistakes: the empty review
   * is honest about itself, and a review never produced says nothing at all.
   */
  readonly complete: boolean;
}

const coverageOf = (scans: readonly Scan[], from: string, to: string): Coverage | null => {
  const { start, end } = boundsOf(from, to);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;

  const completed = scans.filter((scan) => scan.status === 'complete' && scan.completedAt !== null);
  const inPeriod = completed.filter((scan) => {
    const at = Date.parse(scan.completedAt as string);
    return !Number.isNaN(at) && at >= start && at <= end;
  });

  const oldest = scans.reduce<number | null>((earliest, scan) => {
    const at = Date.parse(scan.startedAt);
    if (Number.isNaN(at)) return earliest;
    return earliest === null || at < earliest ? at : earliest;
  }, null);

  return { assessments: inPeriod.length, complete: oldest !== null && oldest <= start };
};

export const ServiceReview = ({ tenantId, scans, onError }: ServiceReviewProps) => {
  // Read once, on mount. A component that called `new Date()` on every render
  // would move its own presets underneath the operator at midnight.
  const [today] = useState(() => new Date());
  const initial = lastQuarter(today);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [busy, setBusy] = useState(false);

  const coverage = useMemo(() => coverageOf(scans, from, to), [scans, from, to]);
  const usable = coverage !== null;

  const open = (print: boolean) => {
    setBusy(true);
    void api
      .openServiceReview(tenantId, from, to, { print })
      .catch((cause: unknown) => onError(message(cause)))
      .finally(() => setBusy(false));
  };

  const applyPreset = (period: { from: string; to: string }) => {
    setFrom(period.from);
    setTo(period.to);
  };

  return (
    <section className="td-stack">
      <p className="lead td-lead">
        A service review is the document you hand a client at the review meeting. Over a period you
        name, it sets out what was put right, what regressed, what is still open and for how long,
        and what could not be assessed. It is built from assessments already completed — producing
        one reads nothing from the client’s tenant and changes nothing.
      </p>

      <div className="card">
        <div className="card-head">
          <h2>Period</h2>
        </div>
        <div className="card-body">
          <div className="toolbar td-review-presets">
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => applyPreset(lastQuarter(today))}
            >
              Last complete quarter
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => applyPreset(lastNinetyDays(today))}
            >
              Last 90 days
            </button>
          </div>

          <div className="td-fields">
            <div className="field">
              <label htmlFor="review-from">From</label>
              <input
                id="review-from"
                type="date"
                value={from}
                aria-describedby="review-period-help"
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="review-to">To</label>
              <input
                id="review-to"
                type="date"
                value={to}
                aria-describedby="review-period-help"
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>

          <p className="hint td-review-help" id="review-period-help">
            Both dates are inside the period: an assessment on the first day or on the last is one
            this review covers. Days are counted in UTC, as every other date in DriftWatch is.
            “Save as PDF” opens the review and your browser’s print dialogue; choose “Save as PDF”
            as the destination to produce the file.
          </p>

          {coverage === null ? (
            <div className="callout callout-caution td-review-coverage">
              <span className="callout-title">That period cannot be read</span>
              Give a start date and an end date, with the start the earlier of the two.
            </div>
          ) : (
            <PeriodCoverage coverage={coverage} />
          )}

          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              aria-busy={busy}
              disabled={busy || !usable}
              onClick={() => open(false)}
            >
              {busy ? 'Opening…' : 'Open service review'}
            </button>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy || !usable}
              onClick={() => open(true)}
            >
              Save as PDF
            </button>
            <span className="status-inline" role="status">
              {busy ? 'The review is opening in a new tab.' : ''}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * How much of the chosen period there is to describe, said before the operator
 * sends the document to anybody.
 *
 * A period with nothing in it is a legitimate answer and not a failure, so this
 * is not an error and the buttons stay live: the review states plainly that the
 * client was not assessed, and there are meetings where that is the thing that
 * has to be said. What it must not do is look like a working document that
 * happens to be short — "we assessed you and all was well" and "we did not
 * assess you" are opposite statements, and the second is the one an operator
 * would be embarrassed to discover in front of a client.
 */
const PeriodCoverage = ({ coverage }: { coverage: Coverage }) => {
  if (coverage.assessments === 0 && coverage.complete) {
    return (
      <div className="callout callout-caution td-review-coverage">
        <span className="callout-title">No assessments in this period</span>
        This client was not assessed at any point between these two dates, so the review will say so
        and will have nothing else to report. That is a document worth sending when the question is
        whether we assessed them — but it is not a quiet quarter, and it must not be read as one.
        Pick a period the client was assessed in to describe how they did.
      </div>
    );
  }

  if (coverage.assessments === 0) {
    return (
      <div className="callout td-review-coverage">
        <span className="callout-title">Nothing recent in this period</span>
        The run log on the Assessments tab does not reach back this far, so the console cannot say
        from here whether this client was assessed between these dates. The review itself reads the
        full history and will say what it finds.
      </div>
    );
  }

  return (
    <p className="note td-review-coverage">
      {coverage.complete ? '' : 'At least '}
      <span className="num">{plural(coverage.assessments, 'completed assessment')}</span> in this
      period.
      {coverage.assessments === 1
        ? ' A single assessment shows where each control stood at that moment; describing a ' +
          'change over the period needs at least two.'
        : ''}
    </p>
  );
};
